import axios from 'axios';
import { getMirrorBaseUrl } from '../utils/mirrorUrl';
import { getAuthAxiosInstance as authAxios } from './axios';
import { MirrorKey, MirrorSchedule, TokenInfo } from '../utils/proposalVerifier';

/** Custom networks go through the backend's mirror proxy, which needs the auth header. */
const mirrorGet = async <T>(network: string, path: string): Promise<T> => {
  const client = network?.startsWith('custom-') ? authAxios() : axios;
  if (!client) throw new Error('Not authenticated');
  const { data } = await client.get(`${getMirrorBaseUrl(network)}${path}`, { timeout: 10_000 });
  if (data == null || typeof data !== 'object') throw new Error(`Empty mirror response for ${path}`);
  return data as T;
};

/** A schedule, including its transaction body, as the mirror node reports it. */
export const getSchedule = (scheduleId: string, network: string) =>
  mirrorGet<MirrorSchedule>(network, `/api/v1/schedules/${scheduleId}`);

/** A token's decimals, symbol and name, as the mirror node reports them. */
export const getTokenInfo = (tokenId: string, network: string) =>
  mirrorGet<TokenInfo>(network, `/api/v1/tokens/${tokenId}`);

/** An account's key, as the mirror node reports it. */
export const getAccountKey = async (accountId: string, network: string) =>
  (await mirrorGet<{ key: MirrorKey }>(network, `/api/v1/accounts/${accountId}`)).key;

/**
 * The transaction with `transactionId` ("0.0.x@seconds.nanos") as the mirror
 * node recorded it, or null while the mirror doesn't have it. The mirror also
 * lists, under the same ID, what the transaction set off: the scheduled
 * transaction a schedule ran, and child records such as an auto-created
 * account. Those are skipped.
 *
 * It lists the payer's transactions from the valid start rather than asking
 * for the ID directly: that answers 404 until the transaction lands, and the
 * browser logs each 404 as an error, once per poll.
 */
export const getSubmittedTransaction = async (
  transactionId: string,
  network: string,
): Promise<{ result: string; entity_id: string | null } | null> => {
  // A "?scheduled" or "/nonce" suffix names one of those other records
  const [account, validStart = ''] = transactionId.split(/[?/]/)[0].split('@');
  const [seconds, nanos = '0'] = validStart.split('.');
  /** "0.0.x-seconds-nanos" with the numbers unpadded, so both spellings compare equal. */
  const normalised = (a: string, s: string, n: string) => `${a}-${Number(s)}-${Number(n)}`;
  const wanted = normalised(account, seconds, nanos);

  const { transactions } = await mirrorGet<{
    transactions?: {
      transaction_id: string;
      result: string;
      entity_id?: string | null;
      scheduled?: boolean;
      nonce?: number;
    }[];
  }>(network, `/api/v1/transactions?account.id=${account}&timestamp=gte:${seconds}.${nanos}&order=asc&limit=100`);
  const submitted = transactions?.find(
    t => normalised(...(t.transaction_id.split('-') as [string, string, string])) === wanted && !t.scheduled && !t.nonce,
  );
  return submitted ? { result: submitted.result, entity_id: submitted.entity_id ?? null } : null;
};
